import axios from "axios";
import API_BASE_URL from "../../config/apiConfig";

const AI_BASE = `${API_BASE_URL}/api/CodingOpportunities/ai`;

// ============================================================
// Cliente AI para Coding Opportunities
// Reemplaza la llamada directa a OpenAI desde el front.
// La API key vive solo en el backend; este cliente solo proxya.
// Controller: CodingOpportunitiesAiController
// Ruta base: /api/CodingOpportunities/ai
// Autenticación: JWT Bearer (todos los endpoints)
// Rate limits por empleado (ver 08_AI_BACKEND_ENDPOINTS.md §1.1):
//   notes-extract   → 60 req / 10 min
//   labs-extract    → 20 req / 10 min
//   coding-assess   → 15 req / 10 min
// ============================================================

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// Whitelist: solo estos 4 campos del paciente viajan al prompt en el backend.
// El resto se ignora al deserializar. El helper acepta el patient completo
// tal cual llega del worklist/detalle y devuelve un objeto limpio.
const extractPatientForAi = (patient) => {
  if (!patient || typeof patient !== "object") return {};
  const out = {};
  if (patient.age != null) out.age = patient.age;
  if (patient.gender) out.gender = patient.gender;
  if (patient.memberType) out.memberType = patient.memberType;
  if (patient.dateOfBirth) out.dateOfBirth = patient.dateOfBirth;

  // Si no viene age y tenemos DOB, calcularlo.
  if (out.age == null && out.dateOfBirth) {
    const dob = new Date(out.dateOfBirth);
    if (!Number.isNaN(dob.getTime())) {
      const now = new Date();
      let a = now.getFullYear() - dob.getFullYear();
      const m = now.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) a--;
      out.age = a;
    }
  }
  return out;
};

// Normaliza errores HTTP del backend para que la UI pueda mostrar mensaje
// humano. Respeta el header Retry-After cuando el back devuelve 429.
const handleError = (error, endpoint) => {
  const status = error?.response?.status;
  if (status === 429) {
    const wait = error?.response?.headers?.["retry-after"] || "60";
    const e = new Error(
      `AI service rate limit exceeded for ${endpoint}. Try again in ${wait} seconds.`
    );
    e.retryAfter = Number(wait) || 60;
    e.status = 429;
    throw e;
  }
  if (status === 400) {
    const msg = error?.response?.data?.message || "Bad request.";
    throw new Error(`AI ${endpoint}: ${msg}`);
  }
  if (status === 401) {
    throw new Error("Session expired. Please log in again.");
  }
  if (status === 500) {
    throw new Error(`AI ${endpoint}: backend error. Try again later.`);
  }
  throw error;
};

/**
 * Extrae findings clínicos de notas (una llamada con el texto de 1+ notas).
 *
 * @param {Object} params
 * @param {string} params.cipId      - CIP del paciente (obligatorio).
 * @param {Object} params.patient    - Patient completo; se extraen solo los 4 campos whitelisted.
 * @param {string} params.notesText  - Resultado de flattenNotesToText(notes).
 *
 * Ejemplo de llamada:
 * POST /api/CodingOpportunities/ai/notes-extract
 * {
 *   "cipId": "45000457653",
 *   "patient": { "age": 72, "gender": "F", "memberType": "ACA", "dateOfBirth": "1953-08-08" },
 *   "notesText": "--- NOTE START ---\n..."
 * }
 *
 * Respuesta exitosa (200):
 * {
 *   "summary": "string",
 *   "key_findings": [
 *     { "condition": "...", "evidence": "...", "date": "...", "source_note_id": "...", "confidence": 0.9, "negated": false }
 *   ],
 *   "coding_relevance": [
 *     { "topic": "...", "why_it_matters": "...", "evidence": "...", "confidence": 0.8 }
 *   ]
 * }
 *
 * Errores:
 *   400 falta cipId o notesText.
 *   401 JWT inválido.
 *   429 rate limit (lanza Error con `retryAfter` en segundos).
 *   500 OpenAI down / JSON malformado.
 */
export const aiNotesExtract = async ({ cipId, patient, notesText }) => {
  try {
    const { data } = await axios.post(
      `${AI_BASE}/notes-extract`,
      {
        cipId,
        patient: extractPatientForAi(patient),
        notesText,
      },
      { headers: authHeaders() }
    );
    return data;
  } catch (error) {
    handleError(error, "notes-extract");
  }
};

/**
 * Extrae findings clínicos de labs (opcional).
 *
 * @param {Object} params
 * @param {string} params.cipId     - CIP.
 * @param {Object} params.patient   - Patient (se filtra al whitelist).
 * @param {string} params.labsText  - Resultado de flattenLabsToText(labs).
 *
 * Ejemplo de llamada:
 * POST /api/CodingOpportunities/ai/labs-extract
 *
 * Respuesta exitosa (200):
 * {
 *   "summary": "string",
 *   "abnormal_labs": [
 *     { "test": "...", "value": "...", "unit": "...", "flag": "...", "date": "...", "interpretation": "...", "confidence": 0.9 }
 *   ],
 *   "coding_relevance": [
 *     { "topic": "...", "why_it_matters": "...", "evidence": "...", "confidence": 0.8 }
 *   ]
 * }
 */
export const aiLabsExtract = async ({ cipId, patient, labsText }) => {
  try {
    const { data } = await axios.post(
      `${AI_BASE}/labs-extract`,
      {
        cipId,
        patient: extractPatientForAi(patient),
        labsText,
      },
      { headers: authHeaders() }
    );
    return data;
  } catch (error) {
    handleError(error, "labs-extract");
  }
};

/**
 * Evalúa Approve / Reject / Needs Review por cada coding opportunity.
 *
 * @param {Object} params
 * @param {string} params.cipId            - CIP.
 * @param {Object} params.patient          - Patient (se filtra al whitelist).
 * @param {string} params.codingOpsText    - Resultado de flattenCodingOpsToText(codingOps).
 * @param {Object} [params.notesSummary]   - JSON devuelto por aiNotesExtract (shape: {summary, key_findings, coding_relevance}).
 * @param {Object|null} [params.labsSummary] - JSON devuelto por aiLabsExtract (shape: {summary, abnormal_labs, coding_relevance}) o null.
 *
 * Ejemplo de llamada:
 * POST /api/CodingOpportunities/ai/coding-assess
 *
 * Respuesta exitosa (200):
 * {
 *   "patient_summary": "string",
 *   "opportunities": [
 *     {
 *       "id": "9012",
 *       "hccId": "HCC19",
 *       "hccDescription": "...",
 *       "diagnosisCode": "E11.9",
 *       "decision": "Approve|Reject|Needs Review",
 *       "confidence": 0.9,
 *       "rationale": "...",
 *       "suggested_dx_codes": [],
 *       "supporting_evidence": [
 *         { "type": "note|lab|other", "quote": "...", "date": "...", "source_id": "..." }
 *       ],
 *       "missing_info": [],
 *       "risk_flags": []
 *     }
 *   ]
 * }
 */
export const aiCodingAssess = async ({
  cipId,
  patient,
  codingOpsText,
  notesSummary,
  labsSummary,
}) => {
  try {
    const { data } = await axios.post(
      `${AI_BASE}/coding-assess`,
      {
        cipId,
        patient: extractPatientForAi(patient),
        codingOpsText,
        notesSummary: notesSummary ?? null,
        labsSummary: labsSummary ?? null,
      },
      { headers: authHeaders() }
    );
    return data;
  } catch (error) {
    handleError(error, "coding-assess");
  }
};
